import {Button, DatePicker, Form, Input, Space} from 'antd';
import {type Dayjs, isDayjs} from 'dayjs';

import {appRoute} from '../../../app-route';
import {Meta} from '../../../component/meta/meta';
import {BreadCrumbs} from '../../../layout/bread-crumbs/bread-crumbs';
import {Page} from '../../../layout/page/page';
import {PageSubHeader} from '../../../layout/page-header/page-sub-header';
import {Locale} from '../../../provider/locale/locale';
import {useLocale} from '../../../provider/locale/locale-hook';
import {DirectorReportPeriodType} from '../../../service/director-report/director-report-api';
import {useSendDirectorReportMutation} from '../../../service/director-report/director-report-hook';
import {useFormRules} from '../../../service/form/form-rules-hook';

type DirectorReportFormType = {
    email: string;
    period?: [Dayjs, Dayjs];
};

function isDayjsRange(value: unknown): value is [Dayjs, Dayjs] {
    return Array.isArray(value) && value.length === 2 && isDayjs(value[0]) && isDayjs(value[1]);
}

export function DirectorReport(): JSX.Element {
    const [form] = Form.useForm<DirectorReportFormType>();
    const {getLocalizedString} = useLocale();
    const {emailFieldRule, requiredFieldRule} = useFormRules();
    const {mutate, isLoading, variables} = useSendDirectorReportMutation();

    async function handleSend(reportType: DirectorReportPeriodType) {
        try {
            await form.validateFields();
        } catch {
            return;
        }

        const period = form.getFieldValue('period');
        const dateRange = isDayjsRange(period)
            ? {startDate: period[0].format('YYYY-MM-DD'), endDate: period[1].format('YYYY-MM-DD')}
            : {};

        mutate({email: form.getFieldValue('email'), reportType, ...dateRange});
    }

    return (
        <Page isTopFilterHidden>
            <Meta title={getLocalizedString('DIRECTOR_REPORT__TITLE')} />

            <BreadCrumbs list={[{path: appRoute.directorReport.path, titleLangKey: 'DIRECTOR_REPORT__TITLE'}]} />

            <PageSubHeader>
                <Locale stringKey="DIRECTOR_REPORT__TITLE" />
            </PageSubHeader>

            <p>
                <Locale stringKey="DIRECTOR_REPORT__DESCRIPTION" />
            </p>

            <Form<DirectorReportFormType> form={form} layout="vertical">
                <Form.Item
                    label={<Locale stringKey="DIRECTOR_REPORT__EMAIL_LABEL" />}
                    name="email"
                    rules={[requiredFieldRule, emailFieldRule]}
                >
                    <Input
                        placeholder={getLocalizedString('DIRECTOR_REPORT__EMAIL_PLACEHOLDER')}
                        size="large"
                        type="email"
                    />
                </Form.Item>

                <Form.Item label={<Locale stringKey="DIRECTOR_REPORT__PERIOD_LABEL" />} name="period">
                    <DatePicker.RangePicker format="YYYY-MM-DD" size="large" />
                </Form.Item>

                <Space wrap>
                    <Button
                        loading={isLoading && variables?.reportType === 'weekly'}
                        onClick={() => handleSend('weekly')}
                        type="primary"
                    >
                        <Locale stringKey="DIRECTOR_REPORT__SEND_WEEKLY" />
                    </Button>
                    <Button
                        loading={isLoading && variables?.reportType === 'monthly'}
                        onClick={() => handleSend('monthly')}
                    >
                        <Locale stringKey="DIRECTOR_REPORT__SEND_MONTHLY" />
                    </Button>
                </Space>
            </Form>
        </Page>
    );
}
